import { useReportLoading } from '../../shared/ReportLead';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Box, Card, CardContent, Stack, Typography, ThemeProvider, useTheme } from '@mui/material';
import { AiTwotoneMail } from 'react-icons/ai';
import { AppVarsContext } from '../../../context/AppVarsContext';
import ReportPerformanceOverview from '../../shared/ReportPerformanceOverview';
import { createReportSurfaceTheme, getReportPageSx, getReportSurfaceSx, analyticsColors } from '../../shared/report-design';
import {
    EmailGallerySection,
    EmailInsightsCard,
    EmailTableSection,
    formatEmailDate,
} from '../../shared/emailReportShared';
import { GlassLoadingState, formatReportDateRangeLabel } from '../../shared/reportStatusCards';

const safeNum = (value) => {
    const num = Number(value);
    return Number.isFinite(num) ? num : 0;
};

const formatNumber = (value) => new Intl.NumberFormat('en-US').format(safeNum(value));
const formatPercent = (value) => `${safeNum(value).toFixed(1)}%`;

export default function MailchimpReport(props) {
    const [campaigns, setCampaigns] = useState([]);
    const [loading, setLoading] = useState(true);
    useReportLoading(loading, props.setLoading);
    const [cid] = useState(sessionStorage.getItem('rid'));
    const brandTheme = useTheme();
    const theme = useMemo(() => createReportSurfaceTheme(brandTheme), [brandTheme]);
    const { appVars, appLoading } = useContext(AppVarsContext);
    const title = props?.title || 'Mailchimp';
    const dateRangeLabel = formatReportDateRangeLabel(sessionStorage.getItem('sDate'), sessionStorage.getItem('eDate'));

    const fetchCampaigns = useCallback(async () => {
        if (!appVars?.api_url || !cid) { setLoading(false); return; }
        setLoading(true);
        try {
            const res = await fetch(`${appVars.api_url}/mailchimp/emails`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    start_date: sessionStorage.getItem('sDate'),
                    end_date: sessionStorage.getItem('eDate'),
                    cid,
                }),
            });
            const data = await res.json();
            setCampaigns(Array.isArray(data?.campaigns) ? data.campaigns : []);

        } catch (error) {
            console.error('Error fetching Mailchimp emails:', error);
            setCampaigns([]);
        } finally {
            setLoading(false);
        }
    }, [appVars?.api_url, cid]);

    useEffect(() => {
        if (!appLoading) {
            fetchCampaigns();
        }
    }, [appLoading, fetchCampaigns]);

    useEffect(() => {
        const handleStorageChange = () => {
            setTimeout(() => fetchCampaigns(), 100);
        };

        window.addEventListener('dc', handleStorageChange);
        return () => window.removeEventListener('dc', handleStorageChange);
    }, [fetchCampaigns]);

    const rows = useMemo(() => campaigns.map((campaign, index) => {
        const sent = safeNum(campaign.emails_sent ?? campaign.sent);
        const delivered = safeNum(campaign.delivered ?? Math.max(0, sent - safeNum(campaign.bounces)));
        const opens = safeNum(campaign.unique_opens ?? campaign.opens);
        const clicks = safeNum(campaign.clicks ?? campaign.unique_clicks);
        const openRate = delivered ? (opens / delivered) * 100 : safeNum(campaign.open_rate) * 100;
        const clickRate = delivered ? (clicks / delivered) * 100 : safeNum(campaign.click_rate) * 100;
        const ctor = opens ? (clicks / opens) * 100 : 0;
        return {
            id: campaign.id || `mailchimp-${index}`,
            name: campaign.subject || campaign.settings?.subject_line || `Mailchimp Email ${index + 1}`,
            sendDate: campaign.send_time || campaign.send_at || campaign.create_time,
            previewText: campaign.preview_text || campaign.settings?.preview_text || '',
            sent,
            delivered,
            opens,
            clicks,
            openRate,
            clickRate,
            ctor,
            raw: campaign,
        };
    }), [campaigns]);

    const summary = useMemo(() => {
        const totals = rows.reduce((acc, row) => {
            acc.sent += row.sent;
            acc.delivered += row.delivered;
            acc.opens += row.opens;
            acc.clicks += row.clicks;
            return acc;
        }, { sent: 0, delivered: 0, opens: 0, clicks: 0 });

        return {
            emails: rows.length,
            totals,
            openRate: totals.delivered ? (totals.opens / totals.delivered) * 100 : 0,
            clickRate: totals.delivered ? (totals.clicks / totals.delivered) * 100 : 0,
            ctor: totals.opens ? (totals.clicks / totals.opens) * 100 : 0,
        };
    }, [rows]);

    const ranked = useMemo(() => {
        const topOpen = [...rows].sort((a, b) => b.openRate - a.openRate)[0] || null;
        const topClick = [...rows].sort((a, b) => b.clickRate - a.clickRate)[0] || null;
        const topClicks = [...rows].sort((a, b) => b.clicks - a.clicks)[0] || null;
        return { topOpen, topClick, topClicks };
    }, [rows]);

    const insightItems = useMemo(() => {
        const items = [];

        if (ranked.topOpen) {
            items.push({
                tone: 'success',
                label: 'Top Open Rate',
                text: `${ranked.topOpen.name} led the range with a ${formatPercent(ranked.topOpen.openRate)} open rate.`,
            });
        }

        if (ranked.topClick) {
            items.push({
                tone: 'success',
                label: 'Top Click Rate',
                text: `${ranked.topClick.name} produced the strongest click rate at ${formatPercent(ranked.topClick.clickRate)}.`,
            });
        }

        if (summary.clickRate < 1) {
            items.push({
                tone: 'warning',
                label: 'CTA Weakness',
                text: `Overall click rate is only ${formatPercent(summary.clickRate)}. Opens are happening, but the content is not pulling enough traffic through.`,
            });
        }

        if (ranked.topClicks && ranked.topClicks.clicks > 0 && ranked.topClicks.clickRate < 1) {
            items.push({
                tone: 'warning',
                label: 'High Click Volume, Low Efficiency',
                text: `${ranked.topClicks.name} drove the most clicks (${formatNumber(ranked.topClicks.clicks)}) but still landed at only ${formatPercent(ranked.topClicks.clickRate)} click rate.`,
            });
        }

        if (!items.length) {
            items.push({
                tone: 'default',
                label: 'Stable Range',
                text: 'Mailchimp email performance stayed steady with no major engagement issues in this period.',
            });
        }

        return items.slice(0, 4);
    }, [ranked, summary.clickRate]);

    const galleryRows = useMemo(() => rows.map((row) => ({
        id: row.id,
        name: row.name,
        sendDate: row.sendDate,
        previewText: row.previewText,
        previewHref: row.raw?.archive_url || '',
        captureUrl: row.raw?.archive_url || '',
        placeholder: 'MC',
        metricItems: [
            { label: 'Sent', value: formatNumber(row.sent) },
            { label: 'Opens', value: `${formatNumber(row.opens)} · ${formatPercent(row.openRate)}` },
            { label: 'Clicks', value: `${formatNumber(row.clicks)} · ${formatPercent(row.clickRate)}` },
            { label: 'CTOR', value: formatPercent(row.ctor) },
        ],
    })), [rows]);



    const tableRows = useMemo(() => [...rows].sort((a, b) => {
        const aTime = new Date(a.sendDate).getTime() || 0;
        const bTime = new Date(b.sendDate).getTime() || 0;
        return bTime - aTime;
    }), [rows]);

    if (loading) {
        return (
            <ThemeProvider theme={theme}><Box sx={getReportPageSx(theme)}>
                <GlassLoadingState
                    icon={<AiTwotoneMail size={34} />}
                    title={`Loading ${title}`}
                    subtitle={dateRangeLabel ? `Pulling send, delivery, open, and click data for ${dateRangeLabel}.` : 'Pulling send, delivery, open, and click data for the selected range.'}
                />
            </Box></ThemeProvider>
        );
    }

    return (
        <ThemeProvider theme={theme}><Box sx={{ minWidth: 0, flex: 1 }}>
            <Box sx={getReportPageSx(theme)}>
                {rows.length > 0 ? (
                    <Box sx={{ position: 'relative', display: 'grid', gap: 2 }}>
                        <ReportPerformanceOverview
                            eyebrow="Mailchimp"
                            title="From delivery to engagement"
                            description="Review delivery, reader engagement, and traffic across your email campaigns."

                            stages={[
                                { label: 'Delivery', description: 'Messages sent and successfully delivered.', metrics: [{ label: 'Sent', value: formatNumber(summary.totals.sent), detail: 'Messages sent in this period' }, { label: 'Delivered', value: formatNumber(summary.totals.delivered), detail: 'Successful deliveries' }] },
                                { label: 'Engagement', description: 'Opens and their share of delivered messages.', metrics: [{ label: 'Opens', value: formatNumber(summary.totals.opens), detail: 'Provider-reported opens' }, { label: 'Open rate', value: formatPercent(summary.openRate), detail: 'Opens / delivered' }] },
                                { label: 'Traffic', description: 'Click activity generated by your emails.', metrics: [{ label: 'Clicks', value: formatNumber(summary.totals.clicks), detail: 'Provider-reported clicks' }, { label: 'Click rate', value: formatPercent(summary.clickRate), detail: 'Clicks / delivered' }] }
                            ]}
                            supportMetrics={[{ label: 'Emails', value: formatNumber(summary.emails) }, { label: 'Click-to-open rate', value: formatPercent(summary.ctor) }]}
                            highlights={[{ label: 'Highest open rate', title: ranked.topOpen?.name || 'No email data', value: formatPercent(ranked.topOpen?.openRate), detail: 'Within the selected emails' }, { label: 'Highest click rate', title: ranked.topClick?.name || 'No email data', value: formatPercent(ranked.topClick?.clickRate), detail: 'Within the selected emails' }]}
                        />

                        <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', xl: '1.1fr 0.9fr' } }}>
                            <Card sx={getReportSurfaceSx(theme)}>
                                <CardContent>
                                    <Stack spacing={1.5}>
                                        <Box>
                                            <Typography variant="h6" sx={{ fontWeight: 800 }}>
                                                Delivery and engagement
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.45 }}>
                                                Delivery is measured against sends; opens and clicks against delivered messages.
                                            </Typography>
                                        </Box>

                                        {[
                                            { label: 'Delivered', value: summary.totals.delivered, denom: summary.totals.sent, color: analyticsColors.acquisition },
                                            { label: 'Opened', value: summary.totals.opens, denom: summary.totals.delivered, color: analyticsColors.engagement },
                                            { label: 'Clicked', value: summary.totals.clicks, denom: summary.totals.delivered, color: analyticsColors.outcome },
                                        ].map((item) => {
                                            const percent = item.denom ? (item.value / item.denom) * 100 : 0;
                                            return (
                                                <Box key={item.label} sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                                                        <Typography variant="body2">{item.label}</Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {formatNumber(item.value)} · {formatPercent(percent)}
                                                        </Typography>
                                                    </Box>
                                                    <Box
                                                        sx={{
                                                            height: 8,
                                                            borderRadius: 999,
                                                            overflow: 'hidden',
                                                            bgcolor: theme.palette.action.hover,
                                                        }}
                                                    >
                                                        <Box
                                                            sx={{
                                                                width: `${Math.min(percent, 100)}%`,
                                                                height: '100%',
                                                                background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                                                            }}
                                                        />
                                                    </Box>
                                                </Box>
                                            );
                                        })}
                                    </Stack>
                                </CardContent>
                            </Card>

                            <EmailInsightsCard
                                title="What Needs Attention"
                                subtitle="The same engagement readout structure used across the email reports."
                                items={insightItems}
                            />
                        </Box>

                        <EmailGallerySection
                            providerLabel="Mailchimp"
                            title="Emails In Range"
                            subtitle="Thumbnail cards for each email in the selected date range."
                            rows={galleryRows}
                        />

                        <EmailTableSection
                            title="Email Performance"
                            subtitle="Consistent delivery and engagement detail for each Mailchimp email."
                            rows={tableRows}
                            emptyMessage="No Mailchimp emails were found in the selected date range."
                            columns={[
                                {
                                    key: 'name',
                                    label: 'Email',
                                    render: (row) => (
                                        <Box>
                                            <Typography variant="body2" sx={{ fontWeight: 700 }}>
                                                {row.name}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {formatEmailDate(row.sendDate)}
                                            </Typography>
                                        </Box>
                                    ),
                                },
                                { key: 'sent', label: 'Sent', align: 'right', render: (row) => formatNumber(row.sent) },
                                { key: 'delivered', label: 'Delivered', align: 'right', render: (row) => formatNumber(row.delivered) },
                                { key: 'opens', label: 'Opens', align: 'right', render: (row) => formatNumber(row.opens) },
                                { key: 'clicks', label: 'Clicks', align: 'right', render: (row) => formatNumber(row.clicks) },
                                { key: 'openRate', label: 'Open Rate', align: 'right', render: (row) => formatPercent(row.openRate) },
                                { key: 'clickRate', label: 'Click Rate', align: 'right', render: (row) => formatPercent(row.clickRate) },
                                { key: 'ctor', label: 'CTOR', align: 'right', render: (row) => formatPercent(row.ctor) },
                            ]}
                        />
                    </Box>
                ) : (
                    <Box
                        sx={{
                            m: 'auto',
                            border: `1px solid ${theme.palette.primary.main}`,
                            borderRadius: 2,
                            background: theme.palette.background.paper,
                        }}
                    >
                        <Box display="flex" flexDirection="column" p={2} alignItems="center" justifyContent="center" width="100%" gap={1}>
                            <AiTwotoneMail size={70} color={theme.palette.primary.main} />
                            <Typography>No Emails</Typography>
                            <Typography>{sessionStorage.getItem('sDate')} and {sessionStorage.getItem('eDate')}</Typography>
                        </Box>
                    </Box>
                )}
            </Box>
        </Box></ThemeProvider>
    );
}
